import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import format from 'date-fns/format'
import { useMemo } from 'react'
import styled, { useTheme } from 'styled-components'

import { Spacing } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { isDocumentUnread } from '../../helpers/document-read-status.helpers'
import {
  StDocumentContent,
  StDocumentIconWrapper,
  StDocumentLink,
  StDot,
  StDotContainer,
  StStatusIndicator,
} from './DocumentLink.styled'

import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import type { PatientViewDocument } from '../../types/patient-documents.types'

export type DocumentLinkProps = {
  document: PatientViewDocument
  selected?: boolean

  onClick?: () => void
}
export const DocumentLink = ({
  document,
  selected,

  onClick,
}: DocumentLinkProps) => {
  const theme = useTheme()

  const showDot = isDocumentUnread(document)

  const iconForFileType = useMemo(() => {
    if (document.type === 'DocumentFlow') {
      return 'signature'
    }

    return 'file'
  }, [document.type])

  const documentFlowStatusIndicator = useMemo(() => {
    if (document.type !== 'DocumentFlow') {
      return null
    }

    const icon: IconProp = document.status === 'completed' ? ['fal', 'check'] : ['far', 'hourglass']
    const iconColor =
      document.documentFlowStatus === 'signed' ? theme.Colors.Status.Success : theme.Colors.Status.Warning

    return (
      <StStatusIndicator signed={document.status === 'completed'}>
        <FontAwesomeIcon icon={icon} color={iconColor} size='xs' />
      </StStatusIndicator>
    )
  }, [document.type, document.status, theme])

  const attachmentExtension = useMemo(() => {
    if (document.type === 'Message') {
      return document.attachments?.[0]?.extension?.replace(/^\./, '') || 'Bijlage'
    }

    if (document.type === 'PatientCommunication') {
      return document.messages?.[0]?.attachments?.[0]?.extension?.replace(/^\./, '') || 'Bijlage'
    }

    return 'Bijlage'
  }, [document])

  return (
    <StDocumentLink selected={selected} onClick={() => onClick?.()}>
      <StDocumentContent>
        {/* Icon */}
        <StDocumentIconWrapper>
          <FontAwesomeIcon icon={['fal', iconForFileType]} />
          {documentFlowStatusIndicator}
        </StDocumentIconWrapper>

        <Spacing width={24} />

        <StDocumentInfo>
          <StDocumentDisplay>
            <Span type={'BodyMediumBold'}>{document.display}</Span>
          </StDocumentDisplay>
          <StDocumentDescription>
            {document.type === 'DocumentFlow' && document.expiresAt && document.status !== 'completed' ? (
              <Span type='BodyMediumRegular' style={{ color: theme.Colors.Status.Warning }}>
                {`${t({
                  id: 'documents.tosign.before',
                  message: 'Te ondertekenen voor',
                })} ${format(new Date(document.expiresAt), 'dd/MM/yyyy')}`}
              </Span>
            ) : document.type === 'DocumentFlow' && document.documentFlowStatus === 'signed' && document.signedAt ? (
              <Span type='BodyMediumRegular' style={{ color: theme.Colors.Status.Success }}>
                {t({
                  id: 'documents.signed.on',
                  message: 'Ondertekend {date} om {time}',
                  values: {
                    date: format(new Date(document.signedAt), 'd/MM/yyyy'),
                    time: format(new Date(document.signedAt), 'HH:mm'),
                  },
                })}
              </Span>
            ) : (
              <>
                <div>{document.date ? format(new Date(document.date), 'dd/MM/yyyy') : <div />}</div>

                <div
                  style={{
                    backgroundColor: theme.Colors.Neutrals.Darker,
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                  }}
                />
                <div style={{ textTransform: 'uppercase' }}>{attachmentExtension}</div>
              </>
            )}
          </StDocumentDescription>
        </StDocumentInfo>
        <Spacing width={12} />
        {/* Dot */}
        <StDotContainer>
          {showDot ? <StDot color={theme.Components.Button.PrimaryWarning.Fill} /> : null}
        </StDotContainer>
      </StDocumentContent>
    </StDocumentLink>
  )
}

const StDocumentInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
`

const StDocumentDisplay = styled.div`
  display: block;
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  font-size: ${({ theme }) => theme.Typography.BodyMediumRegular['font-size']};
  line-height: ${({ theme }) => theme.Typography.BodyMediumRegular['line-height']};
  font-weight: ${({ theme }) => theme.Typography.BodyMediumRegular['font-weight']};
`

const StDocumentDescription = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};

  width: 100%;

  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
  font-size: ${({ theme }) => theme.Typography.BodyMediumRegular['font-size']};
  line-height: ${({ theme }) => theme.Typography.BodyMediumRegular['line-height']};
  font-weight: ${({ theme }) => theme.Typography.BodyMediumRegular['font-weight']};
`
